import { useState, useEffect } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNavigate } from "react-router";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../stores/hooks/hooks.ts";
import {
  fetchPersonaByIdThunk,
  updatePersonaThunk,
  deletePersonaThunk,
  fetchCoreValues,
} from "../../../../../../stores/thunks/personasThunk.tsx";
import { clearSelectedPersona } from "../../../../../../stores/slice/personasSlice.tsx";
import {
  PersonaFormData,
  PersonaUpdatePayload,
  PersonaAPIResponseData,
  CoreValueRequestPersonaData,
} from "../../../../../../stores/types/personasTypes.tsx";
import { PersonaForm } from "./components/PersonaForm.tsx";
import {
  faPencil,
  faStars,
  faTrash,
  faXmark,
} from "@fortawesome/pro-light-svg-icons";
import { PersonaDetailView } from "./PersonaDetailView.tsx";
import {
  RevButton,
  RevDialog,
  type RevDialogFooterBtnListType,
  RevIconButton,
  RevSlideout,
  RevSpinner,
  RevStatusBadge,
  RevUnsavedChangesDialog,
} from "@revmatics/catalyst";
import { NotFoundPage } from "../../../../../../pages/notFound/NotFoundPage.tsx";
import {
  browseMethodOptions,
  buyingMethodOptions,
  familySizeOptions,
  maritalStatusOptions,
} from "./components/data/personaFormData.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const transformPersonaToFormData = (persona: PersonaAPIResponseData | null) => {
  if (!persona) return {};
  return {
    ...persona,
    age_from: persona.age_from?.toString() || "",
    age_to: persona.age_to?.toString() || "",
    marital_status: maritalStatusOptions.find(
      (item) => item.label === persona.marital_status,
    )?.value,
    family_size: familySizeOptions.find(
      (item) => item.label === persona.family_size,
    )?.value,
    preferred_browsing_method: browseMethodOptions.find(
      (item) => item.label === persona.preferred_browsing_method,
    )?.value,
    preferred_buying_method: buyingMethodOptions.find(
      (item) => item.label === persona.preferred_buying_method,
    )?.value,
    locations: {
      included_locations: persona.included_locations || [],
      excluded_locations: persona.excluded_locations || [],
    },
    main_core_value: persona.core_values
      ?.find((v) => v.rank === 1)
      ?.id.toString(),
    secondary_core_value: persona.core_values
      ?.find((v) => v.rank === 2)
      ?.id.toString(),
  };
};

type Props = {
  personaId: string | null;
  setIsOpen: () => void;
};

export const PersonaDetail = ({ personaId, setIsOpen }: Props) => {
  const {
    personaDelete: ffPersonaDelete,
    personaEdit: ffPersonaEdit,
    personaDetails: ffPersonaDetails,
  } = useFlags();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { selectedPersona, isLoadingDetail, error } = useAppSelector(
    (state) => state.personas,
  );
  const currentBrand = useAppSelector((state) => state.brand.currentBrand);
  const { coreValues, isLoadingCoreValues } = useAppSelector(
    (state) => state.personas,
  );

  const isLoading = isLoadingCoreValues || isLoadingDetail;

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [href, setHref] = useState("");
  const [isOpenUnsavedModal, setOpenUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setUnsavedChanges] = useState(false);

  useEffect(() => {
    if (!personaId) {
      setIsEditMode(false);
      dispatch(clearSelectedPersona());
    }
  }, [personaId, dispatch]);

  useEffect(() => {
    if (!coreValues && personaId) {
      dispatch(fetchCoreValues());
    }
  }, [dispatch, coreValues, personaId]);

  const methods = useForm<PersonaFormData>({
    mode: "onSubmit",
  });
  const {
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors: formErrors, dirtyFields },
  } = methods;

  useEffect(() => {
    if (personaId) {
      dispatch(fetchPersonaByIdThunk(personaId));
    }
    return () => {
      dispatch(clearSelectedPersona());
    };
  }, [personaId, dispatch, currentBrand]);

  useEffect(() => {
    if (selectedPersona) {
      const formData = transformPersonaToFormData(selectedPersona);
      reset(formData as unknown as PersonaFormData);
    }
  }, [selectedPersona, reset]);

  const handleDelete = () => {
    if (!personaId) return;
    dispatch(deletePersonaThunk(personaId))
      .unwrap()
      .then(() => {
        navigate("/personas");
      })
      .catch((err) => {
        console.error("Failed to delete persona:", err);
      })
      .finally(() => {
        setIsDeleteDialogOpen(false);
        navigate("/personas");
      });
  };

  const handleConfirmDiscard = () => {
    setOpenUnsavedModal(false);
    setIsEditMode(false);

    if (selectedPersona) {
      const formData = transformPersonaToFormData(selectedPersona);
      reset(formData as unknown as PersonaFormData);
    }
    if (href) {
      navigate(href);
    }
    setHref("");
  };

  const deleteDialogActions: RevDialogFooterBtnListType = [
    <RevButton
      key="cancel"
      text="Cancel"
      variant="secondary"
      handleClick={() => setIsDeleteDialogOpen(false)}
    />,
    <RevButton
      key="delete"
      text="Delete Persona"
      variant="primary"
      buttonColor="error"
      handleClick={handleDelete}
    />,
  ];

  const onSubmit: SubmitHandler<PersonaFormData> = async (data) => {
    if (!personaId && !data.persona_picture) {
      console.error("No personaId for update or picture for create");
      return;
    }
    if (Number(data.income_from) > Number(data.income_to)) {
      setError("income_from", {
        type: "custom",
        message: "'From' cannot be greater than 'To'",
      });
      return;
    }

    setIsSubmitting(true);
    clearErrors("root.serverError");
    const newFileUploaded = data.persona_picture instanceof File;
    const payloadForApi: Partial<PersonaUpdatePayload> = {};

    if (dirtyFields.name) payloadForApi.name = data.name;
    if (dirtyFields.needs) payloadForApi.needs = data.needs;
    if (dirtyFields.concerns) payloadForApi.concerns = data.concerns;
    if (dirtyFields.motivations) payloadForApi.motivations = data.motivations;
    if (dirtyFields.age_from) {
      const ageNum = parseInt(data.age_from as string, 10);
      payloadForApi.age_from = !isNaN(ageNum) ? ageNum : null;
    }
    if (dirtyFields.age_to) {
      const ageNum = parseInt(data.age_to as string, 10);
      payloadForApi.age_to = !isNaN(ageNum) ? ageNum : null;
    }
    if (dirtyFields.gender) payloadForApi.gender = data.gender;
    if (dirtyFields.income_from) payloadForApi.income_from = data.income_from;
    if (dirtyFields.income_to) payloadForApi.income_to = data.income_to;
    if (dirtyFields.education) payloadForApi.education = data.education;
    if (dirtyFields.occupation) payloadForApi.occupation = data.occupation;
    if (dirtyFields.marital_status)
      payloadForApi.marital_status = data.marital_status;
    if (dirtyFields.family_size) payloadForApi.family_size = data.family_size;
    if (dirtyFields.interests) payloadForApi.interests = data.interests;
    if (dirtyFields.preferred_browsing_method)
      payloadForApi.preferred_browsing_method = data.preferred_browsing_method;
    if (dirtyFields.preferred_buying_method)
      payloadForApi.preferred_buying_method = data.preferred_buying_method;
    if (dirtyFields.extroverted_introverted)
      payloadForApi.extroverted_introverted = data.extroverted_introverted;
    if (dirtyFields.serious_playful)
      payloadForApi.serious_playful = data.serious_playful;
    if (dirtyFields.active_sedentary)
      payloadForApi.active_sedentary = data.active_sedentary;
    if (dirtyFields.follower_trendsetter)
      payloadForApi.follower_trendsetter = data.follower_trendsetter;
    if (dirtyFields.spender_saver)
      payloadForApi.spender_saver = data.spender_saver;
    if (dirtyFields.is_platform) payloadForApi.is_platform = data.is_platform;

    if (dirtyFields.locations && data.locations) {
      payloadForApi.included_locations = data.locations.included_locations.map(
        (loc) => loc.id,
      );
      payloadForApi.excluded_locations = data.locations.excluded_locations.map(
        (loc) => loc.id,
      );
    }

    const pictureWasModified =
      dirtyFields.persona_picture || dirtyFields.persona_picture_url;

    if (pictureWasModified) {
      payloadForApi.persona_picture_url = data.persona_picture_url;
      if (!newFileUploaded) {
        payloadForApi.persona_picture = data.persona_picture;
      }
    }

    if (dirtyFields.main_core_value || dirtyFields.secondary_core_value) {
      payloadForApi.core_values = [];

      if (data.main_core_value) {
        payloadForApi.core_values.push({
          core_value_id: Number(data.main_core_value),
          rank: 1,
        });
      }

      if (data.secondary_core_value) {
        payloadForApi.core_values.push({
          core_value_id: Number(data.secondary_core_value),
          rank: 2,
        });
      }
    }

    if (Object.keys(payloadForApi).length === 0 && !newFileUploaded) {
      setIsEditMode(false);
      setIsSubmitting(false);
      return;
    }

    let finalPayload: FormData | Partial<PersonaUpdatePayload>;
    const formDataPayload = new FormData();

    if (newFileUploaded && data.persona_picture) {
      finalPayload = new FormData();
      Object.entries(payloadForApi).forEach(([key, value]) => {
        if (value === null) {
          formDataPayload.append(key, "");
        } else if (Array.isArray(value) && key === "core_values") {
          value.forEach((item, index) => {
            const coreValueItem = item as CoreValueRequestPersonaData;
            formDataPayload.append(
              `core_values[${index}]core_value_id`,
              String(coreValueItem.core_value_id),
            );
            formDataPayload.append(
              `core_values[${index}]rank`,
              String(coreValueItem.rank),
            );
          });
        } else if (Array.isArray(value)) {
          value.forEach((item) => formDataPayload.append(key, String(item)));
        } else if (value !== undefined) {
          formDataPayload.append(key, String(value));
        }
      });
      formDataPayload.append("persona_picture", data.persona_picture);

      finalPayload = formDataPayload;
    } else {
      finalPayload = payloadForApi;
    }
    dispatch(
      updatePersonaThunk({
        id: Number(personaId),
        data: finalPayload,
        callback: () => {
          if (hasUnsavedChanges) {
            if (href) {
              navigate(href);
            } else {
              setIsEditMode(false);
              const formData = transformPersonaToFormData(selectedPersona);
              reset(formData as unknown as PersonaFormData);
            }
          }
        },
      }),
    )
      .unwrap()
      .then((updatedPersona) => {
        reset({
          ...updatedPersona,
          age_from: updatedPersona.age_from?.toString() || "",
          age_to: updatedPersona.age_to?.toString() || "",
          main_core_value: updatedPersona.core_values
            ?.find((v) => v.rank === 1)
            ?.id.toString(),
          secondary_core_value: updatedPersona.core_values
            ?.find((v) => v.rank === 2)
            ?.id.toString(),
        } as unknown as PersonaFormData);
        setIsEditMode(false);
      })
      .catch(() => {
        setError("root.serverError", {
          type: "server",
          message: "Failed to update persona.",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <RevSlideout
      isOpen={!!personaId}
      setIsOpen={setIsOpen}
      size="lg"
      headerComponent={
        <div className="flex justify-between px-4 py-6 sm:px-6 border-b border-monochrome-100">
          <div className="flex gap-2 items-center">
            <h5>Persona Details</h5>
            {selectedPersona?.is_ai_generated && (
              <div>
                <div className="hidden md:block">
                  <RevStatusBadge
                    text="AI Generated"
                    icon={faStars}
                    color="ai"
                  />
                </div>
                <div className="md:hidden">
                  <FontAwesomeIcon
                    icon={faStars}
                    className="typography-body-md text-purple-600"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {ffPersonaDelete && !isEditMode && (
              <RevIconButton
                variant="outline"
                size="l"
                icon={faTrash}
                handleClick={() => setIsDeleteDialogOpen(true)}
                ariaLabel="Delete persona slideout"
              />
            )}
            {ffPersonaEdit && !isEditMode && (
              <RevIconButton
                variant="outline"
                size="l"
                icon={faPencil}
                handleClick={() => {
                  const formData = transformPersonaToFormData(selectedPersona);
                  reset(formData as unknown as PersonaFormData);
                  setIsEditMode(true);
                }}
                ariaLabel="Edit persona slideout"
              />
            )}
            <RevIconButton
              icon={faXmark}
              variant="outline"
              size="l"
              handleClick={setIsOpen}
              ariaLabel="Close slideout"
            />
          </div>
        </div>
      }
    >
      {isLoading && (
        <div className="h-full">
          <RevSpinner size="large" color="primary" centered />
        </div>
      )}
      {(!selectedPersona || error || !ffPersonaDetails) && !isLoading && (
        <NotFoundPage />
      )}
      {selectedPersona && ffPersonaDetails && !error && !isLoading && (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-primary-25 pt-4 pb-8 px-6">
              {isEditMode ? (
                <PersonaForm
                  setHref={setHref}
                  setOpenUnsavedModal={setOpenUnsavedModal}
                  setUnsavedChanges={setUnsavedChanges}
                  hasUnsavedChanges={hasUnsavedChanges}
                />
              ) : (
                <PersonaDetailView persona={selectedPersona} />
              )}
              {formErrors.root?.serverError && (
                <p className="typography-body-sm text-red-500 mt-2">
                  {formErrors.root.serverError.message}
                </p>
              )}
              <RevUnsavedChangesDialog
                pageId="profile-settings-page"
                isOpen={isOpenUnsavedModal}
                setIsOpen={(open) => {
                  if (!open) setHref("");
                  setOpenUnsavedModal(open);
                }}
                onSave={handleSubmit(onSubmit)}
                onDiscard={handleConfirmDiscard}
                isLoading={isSubmitting}
                classNameContainer="z-50"
              />
              <RevDialog
                isOpen={isDeleteDialogOpen}
                setIsOpen={setIsDeleteDialogOpen}
                title="Delete Persona"
                btnList={deleteDialogActions}
                classNameContainer="z-50"
              >
                <p className="typography-body-sm text-monochrome-900">
                  Are you sure you want to delete the persona "
                  {selectedPersona.name}
                  "? This action can't be undone.
                </p>
              </RevDialog>
            </div>
            {isEditMode && (
              <div className="grid grid-cols-2 sm:flex sm:justify-end gap-3 sticky bottom-0 px-6 py-4 bg-white shadow-elevation--1 z-10">
                <RevButton
                  type="button"
                  text="Cancel"
                  variant="secondary"
                  buttonSize="medium"
                  handleClick={() => {
                    if (hasUnsavedChanges) {
                      setOpenUnsavedModal(true);
                      return;
                    }
                    setIsEditMode(false);
                    const formData =
                      transformPersonaToFormData(selectedPersona);
                    reset(formData as unknown as PersonaFormData);
                  }}
                />
                <RevButton
                  type="submit"
                  text="Save"
                  buttonSize="medium"
                  variant="primary"
                  isLoading={isSubmitting}
                />
              </div>
            )}
          </form>
        </FormProvider>
      )}
    </RevSlideout>
  );
};
